import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  MenuItem,
  SharedModule,
  TreeDragDropService,
  TreeNode,
} from 'primeng/api';
import { TreeModule, TreeNodeSelectEvent } from 'primeng/tree';
import { NgClass, NgIf } from '@angular/common';
import { IndicatorTreeNode } from '../../../models/IndicatorTreeNode';
import { CategoryStore } from '../../../stores/category/category.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { Indicator } from '../../../models/indicator';
import { getState } from '@ngrx/signals';
import { ContextMenuModule } from 'primeng/contextmenu';
import { SidebarModule } from 'primeng/sidebar';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorData } from '../../../models/indicator-data';
import { IndicatorTreeHelperService } from '../../../indicator-tree-helper.service';
import { StateFilter } from '../../../stores/crud.state';

@Component({
  selector: 'app-atlas-indicator-tree',
  standalone: true,
  imports: [
    SharedModule,
    TreeModule,
    NgClass,
    NgIf,
    ContextMenuModule,
    SidebarModule,
  ],
  templateUrl: './atlas-indicator-tree.component.html',
  styleUrl: './atlas-indicator-tree.component.scss',
  providers: [TreeDragDropService],
})
export class AtlasIndicatorTreeComponent implements OnInit, OnDestroy {
  @Input() public overrideTree?: IndicatorTreeNode[];
  @Input() overrideWithIndicators?: Indicator[];
  @Input() public global = false;
  @Input() public combineableIndicatorIds: string[] | null = null;
  @Input() max = 999;
  @Input() min = 1;
  @Input() withDataInType?: string;
  @Input() selection: string[] = [];
  @Output() selectIndicator = new EventEmitter<string>();
  readonly widgetStore = inject(WidgetStore);
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);

  typeId?: string;
  availableData: IndicatorData[] = [];
  indicatorIdFilter: string[] = [];
  selectedIndicator?: Indicator;
  sessionStorage: Storage | undefined;
  cachedExpandedStates: { [key: string]: boolean } = {};
  selectedNode: any;
  initialized = false;
  groupedIndicators: Record<string, Indicator[]> = {};
  groupedIndicatorsWithData: Record<string, Indicator[]> = {};
  categoryTree: IndicatorTreeNode[] = [];
  items!: MenuItem[];
  tree: IndicatorTreeNode[] = [];
  private filterBeforeInit?: StateFilter;

  constructor(
    private treeHelper: IndicatorTreeHelperService
    //private messageService: ConfirmationDialogService
  ) {
    // inject local storage
    this.sessionStorage = document.defaultView?.sessionStorage;
    effect(() => {
      const widgetState = getState(this.widgetStore);
      const indicatorState = getState(this.indicatorStore);
      const indicatorDataState = getState(this.indicatorDataStore);
      const categoryState = getState(this.categoryStore);
      if (!this.overrideTree) this.rerenderTree();
    });
  }

  /**
   * Sets the expanded state for a tree component by retrieving
   * the data from local storage and parsing it to update the
   * cachedExpandedStates property.
   *
   * @return {void} This method does not return any value.
   */
  setExpandedState(): void {
    this.cachedExpandedStates = JSON.parse(
      this.sessionStorage?.getItem('atlasTreeExpandedState') || '{}'
    );
  }

  /**
   * Initializes the component by setting up necessary data when it is loaded.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.treeHelper.initTreeService();
    if (!this.overrideTree) {
      this.setAvailableData();
    } else {
      this.tree = structuredClone(this.overrideTree);
    }
  }

  /**
   * Sets the available data based on the currently selected widget's data layer type.
   * Filters the data, retrieves relevant indicator IDs, and updates the indicator store's filter.
   *
   * @return {void} This method does not return a value.
   */
  setAvailableData(): void {
    // an explicitly requested type wins over the selected widget's type
    this.typeId =
      this.withDataInType || this.widgetStore.selected()?.widget?.dataLayerType;
    if (this.typeId) {
      this.filterBeforeInit ??= { ...this.indicatorStore.filter() };

      this.availableData = this.indicatorDataStore.filterByDataLayerType(
        this.typeId
      );

      const indicatorIdsWithoutSets = this.availableData.map(
        item => item.indicator
      );

      this.indicatorIdFilter = indicatorIdsWithoutSets.concat(
        this.indicatorStore.getSetIdsByIds(indicatorIdsWithoutSets)
      );

      this.indicatorStore.updateFilter({
        query: '',
        ids: this.indicatorIdFilter,
        locked: null,
        published: null,
        category: [],
        tags: [],
        products: [],
      });
    }
  }

  /**
   * Restores the indicator filter that was active before this tree narrowed
   * it down, so the shared filter doesn't leak into other lists (e.g. the
   * global search).
   *
   * @return {void} This method does not return a value.
   */
  ngOnDestroy(): void {
    if (this.filterBeforeInit) {
      this.indicatorStore.updateFilter(this.filterBeforeInit);
    }
  }

  /**
   * Rerenders the tree structure by resetting the expanded state, grouping indicators,
   * and updating the category tree with filtered data. The method ensures that the
   * category tree is updated to reflect the available indicators and their associations.
   *
   * @return {void} This method does not return a value.
   */
  rerenderTree(): void {
    this.setExpandedState();
    let groupedChildrenOverride: Record<string, Indicator[]> | undefined =
      undefined;

    if (this.overrideWithIndicators) {
      const overwrittenIds = this.overrideWithIndicators.map(item => item._id);
      const parentsToConcat: Indicator[] = [];
      this.overrideWithIndicators.forEach(indicator => {
        if (indicator.parent && !overwrittenIds.includes(indicator.parent)) {
          parentsToConcat.push(this.indicatorStore.getById(indicator.parent));
        }
      });

      const allIndicators = this.overrideWithIndicators.concat(parentsToConcat);
      this.groupedIndicators =
        this.indicatorStore.groupByCategories(allIndicators);

      groupedChildrenOverride = allIndicators.reduce(
        (acc, indicator) => {
          const { parent } = indicator;
          if (!parent) return acc;
          if (!acc[parent]) acc[parent] = [];
          acc[parent].push(indicator);
          return acc;
        },
        {} as Record<string, Indicator[]>
      );
    } else {
      this.groupedIndicators = this.indicatorStore.groupedByCategory();
    }

    const initialCategoryTree = this.categoryStore.asTree();
    if (initialCategoryTree && initialCategoryTree.length) {
      this.initialized = true;
      this.groupedIndicatorsWithData = { ...this.groupedIndicators };

      setTimeout(() => {
        const filteredCategoryTree =
          this.treeHelper.filterCategoriesWithIndicators(
            initialCategoryTree,
            this.groupedIndicatorsWithData
          );

        this.tree = this.treeHelper.mergeIndicatorsWithCategoryTree(
          filteredCategoryTree,
          this.groupedIndicatorsWithData,
          false,
          groupedChildrenOverride
        );

        this.treeHelper.applyNumbering(this.tree);
      });
    }
  }

  /**
   * Handles the selection of a specified tree node in the indicator tree.
   * Emits the selected node's ID if the global state is not active.
   *
   * @param {IndicatorTreeNode} node - The tree node to be selected.
   * @return {void} This method does not return a value.
   */
  nodeSelect(node: IndicatorTreeNode): void {
    if (!this.global) {
      this.selectIndicator.emit(node._id);
    }
  }

  /**
   * Toggles the expanded state of a tree node and updates the tree's expanded state if the node has a key.
   *
   * @param {TreeNode} node - The tree node whose expanded state is to be toggled.
   * @return {void} No return value.
   */
  toggleExpand(node: TreeNode): void {
    node.expanded = !node.expanded;
    if (node.key) this.updateTreeExpandedState(node.key, node.expanded);
  }

  /**
   * Updates the expanded state of a tree node and stores the state in local storage.
   * The expanded state is saved using a key-value pair where the key is the node identifier
   * and the value is the boolean representation of its expanded or collapsed state.
   *
   * @param {string} key - The identifier of the tree node whose expanded state is being updated.
   * @param {boolean} isExpanded - The new expanded state of the tree node (true for expanded, false for collapsed).
   * @return {void}
   */
  updateTreeExpandedState(key: string, isExpanded: boolean): void {
    const currentStates = JSON.parse(
      this.sessionStorage?.getItem('atlasTreeExpandedState') || '{}'
    );
    currentStates[key] = isExpanded;
    this.sessionStorage?.setItem(
      'atlasTreeExpandedState',
      JSON.stringify(currentStates)
    );

    this.treeHelper.setExpandedState();
  }

  /**
   * Tracks an `IndicatorTreeNode` by its unique `_id` property.
   *
   * @param {number} index - The index of the current node in the iteration.
   * @param {IndicatorTreeNode} node - The node object to track.
   * @return {string} The unique `_id` of the given node.
   */
  trackByNodeId(index: number, node: IndicatorTreeNode): string {
    if (this.overrideTree) {
      return node.key;
    } else {
      return node._id;
    }
  }

  /**
   * Determines whether a given node can be combined with existing selections or criteria.
   * Provides a*/
  cannotCombine(node: IndicatorTreeNode): false | string {
    if (
      this.combineableIndicatorIds &&
      !this.combineableIndicatorIds.includes(node._id)
    ) {
      return 'Daten nicht kombinierbar';
    }

    const indicatorSelection = this.widgetStore.activeIndicatorSelection();
    const firstSelection = indicatorSelection[0]
      ? this.indicatorStore.getById(indicatorSelection[0])
      : null;

    if (
      firstSelection &&
      firstSelection.unit &&
      node.unit &&
      String(node.unit) !== String(firstSelection.unit)
    ) {
      return 'Einheit unterscheidet sich';
    }

    return false;
  }

  /**
   * Handles the selection of a tree node and performs specific actions based on the node type.
   *
   * @param {TreeNodeSelectEvent} $event - The event triggered upon selection of a tree node. Contains information about the selected node.
   * @return {void} This method does not return any value.
   */
  treeNodeSelect($event: TreeNodeSelectEvent) {
    const node = $event.node as IndicatorTreeNode;

    switch (node.type) {
      case 'indicator':
        this.nodeSelect(node);
        break;
      case 'category':
      case 'indicator-set':
        this.toggleExpand(node);
        break;
    }
  }
}
